/**
 * DataGrid cell rendering utilities
 */

import { Box } from "@mui/material";
import { GridRenderCellParams } from "@mui/x-data-grid-premium";
import { RiExternalLinkLine } from "@remixicon/react";
import React from "react";

import {
  formatDateHumanReadable,
  formatDateYYYYMMDD,
  formatFileSize,
  formatNumber,
  formatNumberPct,
} from "../formatting";
import { formatDurationFromSecs } from "../formatting/timeFormatters";

/**
 * Renders a cell with text wrapping and proper overflow handling
 * @param params - DataGrid render cell parameters
 * @returns React element with wrapped text display
 */
export const renderWrappedCell = (
  params: GridRenderCellParams
): React.ReactElement => {
  return React.createElement(
    Box,
    {
      sx: {
        alignItems: "center",
        whiteSpace: "normal",
        wordWrap: "break-word",
        overflow: "auto",
        maxHeight: "none",
        lineHeight: "1.2",
        height: "100%",
      },
    },
    params.value !== undefined && params.value !== null && params.value !== ""
      ? String(params.value)
      : "--"
  );
};

/**
 * Renders a date cell in YYYY-MM-DD format
 * @param params - DataGrid render cell parameters
 * @param defaultValue - Value to display if date is invalid (default: "--")
 * @returns Formatted date string
 */
export const renderDateCell = (
  params: GridRenderCellParams,
  defaultValue: string = "--"
) => {
  return params.value && params.value > new Date(0)
    ? formatDateYYYYMMDD(new Date(params.value))
    : defaultValue;
};

/**
 * Renders a timestamp cell with timezone information
 * @param params - DataGrid render cell parameters
 * @param defaultValue - Value to display if timestamp is invalid (default: "--")
 * @returns Formatted timestamp string with timezone
 */
export const renderTimestampCell = (
  params: GridRenderCellParams,
  defaultValue: string = "--"
) => {
  if (!params.value || new Date(params.value) <= new Date(0)) {
    return defaultValue;
  }

  const date = new Date(params.value);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  const timezoneOffset = -date.getTimezoneOffset();
  const offsetSign = timezoneOffset >= 0 ? "+" : "-";
  const offsetHours = Math.abs(Math.floor(timezoneOffset / 60));
  const offsetMinutes = Math.abs(timezoneOffset % 60);

  const formattedOffset = `${offsetSign}${offsetHours}:${String(
    offsetMinutes
  ).padStart(2, "0")}`;

  return `${year}-${month}-${day} ${hours}:${minutes} (${formattedOffset})`;
};

/**
 * Renders a date cell in human-readable format
 * @param params - DataGrid render cell parameters
 * @param defaultValue - Value to display if date is invalid (default: "--")
 * @returns Human-readable formatted date string
 */
export const renderHumanReadableDateCell = (
  params: GridRenderCellParams,
  defaultValue: string = "--"
) => {
  return params.value && params.value > new Date(0)
    ? formatDateHumanReadable(new Date(params.value))
    : defaultValue;
};

/**
 * Renders a number cell with proper formatting
 * @param params - DataGrid render cell parameters
 * @param defaultValue - Value to display if number is invalid (default: "--")
 * @returns Formatted number string
 */
export const renderNumberCell = (
  params: GridRenderCellParams<Number>,
  defaultValue: string = "--"
) => {
  if (params.value === undefined || params.value === null) {
    return defaultValue;
  }
  return formatNumber(params.value);
};

/**
 * Renders a duration cell from seconds with proper formatting
 * @param params - DataGrid render cell parameters
 * @param defaultValue - Value to display if number is invalid (default: "--")
 * @returns Formatted duration string
 */
export const renderDurationFromSecsCell = (
  params: GridRenderCellParams<Number>,
  defaultValue: string = "--"
) => {
  if (params.value === undefined || params.value === null) {
    return defaultValue;
  }
  return formatDurationFromSecs(params.value);
};

/**
 * Renders a percentage cell with specified precision
 * @param params - DataGrid render cell parameters
 * @param precision - Number of decimal places (default: 2)
 * @param defaultValue - Value to display if number is invalid (default: "--")
 * @returns Formatted percentage string
 */
export const renderPercentCell = (
  params: GridRenderCellParams<Number>,
  precision: number = 2,
  defaultValue: string = "--"
) => {
  if (params.value === 0 || params.value === -0) {
    return "0.00%";
  }

  if (params.value === undefined || params.value === null) {
    return defaultValue;
  }
  return formatNumberPct(params.value, precision);
};

/**
 * Renders a file size cell with proper formatting
 * @param params - DataGrid render cell parameters
 * @param defaultValue - Value to display if number is invalid (default: "--")
 * @returns Formatted file size string
 */
export const renderFileSizeCell = (
  params: GridRenderCellParams<Number>,
  defaultValue: string = "--"
) => {
  if (params.value === undefined || params.value === null) {
    return defaultValue;
  }
  return formatFileSize(params.value);
};

/**
 * Renders an external link cell with icon
 * @param params - DataGrid render cell parameters
 * @param customLabel - Custom label for the link (optional)
 * @returns React node with link and external icon
 */
export const renderExternalLinkCell = (
  params: GridRenderCellParams,
  customLabel?: string
): React.ReactNode => {
  const link = params.value;

  if (!link) {
    return React.createElement("span", null, "--");
  }

  return React.createElement(
    "span",
    null,
    React.createElement(
      "a",
      {
        href: link,
        target: "_blank",
        rel: "noopener noreferrer",
      },
      customLabel || link
    ),
    React.createElement(RiExternalLinkLine, {
      style: {
        marginLeft: 5,
        verticalAlign: "middle",
        width: "14px",
      },
    })
  );
};
